/**
 * ==========================================================================
 * featured-collection-lazy-custom.js
 * --------------------------------------------------------------------------
 * featured-collection-custom セクションの「タブ遅延描画(lazy-tab)」用スクリプト
 *
 * 方式（確実動作版 2026-06-09 改）:
 *  - 初期HTMLは先頭タブのみフル描画。2枚目以降は空パネル(data-fc-lazy + hidden + スピナー)。
 *  - 初回にいずれかの空タブが押されたら、「通常ページ」として /?fc_lazyall=1 を1回だけ取得する。
 *    （Section Rendering API(?section_id=)ではpageurlにクエリが乗らずパラメータが渡らないため、
 *     通常ページリクエストにすることで fc_lazyall を確実に Liquid 側へ渡す）
 *  - セクション側は fc_lazyall を検知すると全タブをフル描画して返す。
 *  - 取得結果から全 product-tabs セクションの全パネルHTMLをキャッシュし、押されたパネルへ注入。
 *  - 以降のタブ切替はキャッシュから即時注入（再フェッチ無し）。
 *  - フェッチ中は Liquid 側のスピナーがそのまま表示される。失敗時はエラー+「再読み込み」ボタン。
 *
 * 注入された <featured-collection> は DOM 接続時に connectedCallback が走り Swiper を初期化する。
 * パネルは表示状態で注入されるため寸法も正しく取得できる。
 * ==========================================================================
 */
(function () {
  'use strict';

  var ALL_PARAM = 'fc_lazyall=1';
  var cache = null; // { "sectionId:index": innerHTML }
  var fetchPromise = null;

  // スピナー用 keyframes を一度だけ注入（パネル表示前=DOMContentLoaded時点で確実に存在させる）
  function injectKeyframes() {
    if (document.getElementById('fc-lazy-style')) return;
    var st = document.createElement('style');
    st.id = 'fc-lazy-style';
    st.textContent =
      '@keyframes fc-lazy-spin{to{transform:rotate(360deg)}}' +
      '.featured-collection__lazy--error{min-height:160px;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:10px;}' +
      '.fc-lazy-retry{cursor:pointer;text-decoration:underline;background:none;border:none;font:inherit;color:inherit;}';
    (document.head || document.documentElement).appendChild(st);
  }

  function buildUrl() {
    var path = window.location.pathname || '/';
    var search = window.location.search || '';
    if (search) {
      return path + search + (search.indexOf('?') === 0 ? '&' : '?') + ALL_PARAM;
    }
    return path + '?' + ALL_PARAM;
  }

  // /?fc_lazyall=1 を1回だけ取得し、全 product-tabs セクションの全パネルをキャッシュ
  function fetchAll() {
    if (fetchPromise) return fetchPromise;
    fetchPromise = fetch(buildUrl(), { credentials: 'same-origin' })
      .then(function (res) {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var map = {};
        var sections = doc.querySelectorAll('[is="product-tabs"][data-section-id]');
        for (var i = 0; i < sections.length; i++) {
          var sec = sections[i];
          var sid = sec.getAttribute('data-section-id');
          var panels = sec.querySelectorAll('.tabs__panel[data-index]');
          for (var j = 0; j < panels.length; j++) {
            var p = panels[j];
            // フル描画されたパネルのみ（featured-collection を内包するもの）をキャッシュ
            if (p.querySelector('featured-collection')) {
              map[sid + ':' + p.getAttribute('data-index')] = p.innerHTML;
            }
          }
        }
        cache = map;
        return map;
      })
      .catch(function (err) {
        fetchPromise = null; // 失敗時は次回再試行を許可
        throw err;
      });
    return fetchPromise;
  }

  function injectFromMap(panel, sectionId, map) {
    var key = sectionId + ':' + panel.getAttribute('data-index');
    var html = map && map[key];
    if (!html) {
      showError(panel, sectionId);
      return;
    }
    panel.innerHTML = html; // featured-collection は接続時に Swiper を初期化（表示中なので寸法OK）
    panel.removeAttribute('data-fc-lazy');
    panel.removeAttribute('aria-busy');
    panel.__fcLoading = false;
  }

  function fillPanel(panel, sectionId) {
    // 既にキャッシュ済みなら即時注入
    if (cache) {
      injectFromMap(panel, sectionId, cache);
      return;
    }
    // 初回: フェッチ（この間 Liquid のスピナーが表示され続ける）
    panel.__fcLoading = true;
    fetchAll()
      .then(function (map) {
        injectFromMap(panel, sectionId, map);
      })
      .catch(function () {
        showError(panel, sectionId);
      });
  }

  function showError(panel, sectionId) {
    panel.__fcLoading = false;
    panel.removeAttribute('aria-busy');
    panel.innerHTML =
      '<div class="featured-collection__lazy featured-collection__lazy--error">' +
      '<span style="opacity:.7;">読み込みに失敗しました</span>' +
      '<button type="button" class="fc-lazy-retry">再読み込み</button>' +
      '</div>';
    var btn = panel.querySelector('.fc-lazy-retry');
    if (btn) {
      btn.addEventListener('click', function () {
        panel.setAttribute('data-fc-lazy', '');
        panel.setAttribute('aria-busy', 'true');
        panel.innerHTML =
          '<div class="featured-collection__lazy" style="min-height:220px;display:flex;align-items:center;justify-content:center;">' +
          '<span class="fc-lazy-spinner" role="status" aria-label="読み込み中" style="width:34px;height:34px;border:3px solid rgba(0,0,0,.12);border-top-color:rgba(0,0,0,.55);border-radius:50%;display:inline-block;animation:fc-lazy-spin .8s linear infinite;"></span>' +
          '</div>';
        fillPanel(panel, sectionId);
      });
    }
  }

  function bindWrapper(wrapper) {
    if (!wrapper || wrapper.__fcLazyBound) return;
    var sectionId = wrapper.getAttribute('data-section-id');
    if (!sectionId) return;
    wrapper.__fcLazyBound = true;

    // product-tabs(コア) がタブ表示後に発火する tabChange を購読
    wrapper.addEventListener('tabChange', function (e) {
      var panel = e && e.detail && e.detail.selectedTab;
      if (!panel) return;
      if (!panel.hasAttribute('data-fc-lazy')) return; // 既ロード or 先頭タブ
      if (panel.__fcLoading) return;
      fillPanel(panel, sectionId);
    });
  }

  function boot(root) {
    injectKeyframes();
    var scope = root && root.querySelectorAll ? root : document;
    var wrappers = scope.querySelectorAll('[is="product-tabs"][data-section-id]');
    for (var i = 0; i < wrappers.length; i++) bindWrapper(wrappers[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      boot(document);
    });
  } else {
    boot(document);
  }

  // テーマエディタでのセクション再読込に対応
  document.addEventListener('shopify:section:load', function (e) {
    boot(e.target || document);
  });
})();
